/**
 * isiXhosa: Contacts
 *
 * What this file covers: The contact book: contacts, fields, tags, segments, imports, consent and erasing a
 * person's data.
 *
 * Tone: Clear and careful. Consent and erasure wording is legal in effect: say exactly what happens to the
 * person's data.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const contacts: Catalog = {
  'contacts.title': 'Abaqhagamshelwana nabo',
  'contacts.optedIn': 'Uvumile',
  'contacts.optedOut': 'Urhoxisile',
  'contacts.actions.importCsv': 'Ngenisa i-CSV',
  'contacts.loadError': 'Ayikwazanga ukulayisha abaqhagamshelwana nabo.',
  'contacts.shown': '{{count}} iboniswe',
  'contacts.columns.optIn': 'Ukuvuma',
  'contacts.optIn.in': 'Uvumile',
  'contacts.optIn.out': 'Urhoxile',
  'contacts.modal.delete.title': 'Cima umqhagamshelwano?',
  'contacts.toast.importQueuedTitle': 'Ukungenisa i-CSV',
  'contacts.toast.importFailed': 'Ukungenisa kusilele',
  'contacts.drawer.new': 'Umqhagamshelwano omtsha',
  'contacts.drawer.edit': 'Hlela umqhagamshelwano',
  'contacts.form.tagsHint': 'Cofa u-Enter emva kwethegi nganye.',
  'contacts.form.optIn': 'Uvumile',
  'contacts.view.notFound': 'Umqhagamshelwano awufunyanwanga.',
  'contacts.view.header.noOptIn': 'akukho kuvuma',
  'contacts.view.backToAll': 'Bonke abaqhagamshelwana nabo',
  'contacts.view.tabs.profile': 'Iprofayile',
  'contacts.view.tabs.conversations': 'Iincoko',
  'contacts.view.tabs.danger': 'Ingozi',
  'contacts.view.profile.locale': 'Indawo',
  'contacts.view.tags.empty': 'Akukho thegi okwangoku.',
  'contacts.view.conversations.empty': 'Akukho ncoko nalo mqhagamshelwano.',
  'contacts.view.conversations.noPreview': '(akukho mboniso)',
  'contacts.view.danger.markOptOut': 'Phawula njengorhoxileyo',
  'contacts.view.danger.markOptIn': 'Phawula njengovumileyo',
  'contacts.view.danger.deleteContact': 'Cima umqhagamshelwano',
  'contacts.view.danger.modal.delete.irreversible': 'Oku akunakuguqulwa.',
  'contacts.view.danger.toast.deleteFailed': 'Ayikwazanga ukucima umqhagamshelwano.',
  'contacts.actions.import': 'Ngenisa',
  'contacts.actions.create': 'Yenza uqhagamshelwano',
  'contacts.export.csv': 'Khuphela njenge-CSV',
  'contacts.export.xlsx': 'Khuphela njenge-Excel (.xlsx)',
  'contacts.columns.contact': 'Uqhagamshelwano',
  'contacts.filters.clear': 'Cima izihluzi',
  'contacts.filters.tag.option': '{{tag}} ({{count}})',
  'contacts.rowsPerPage': 'Imigca ngephepha',
  'contacts.form.tab.details': 'Iinkcukacha',
  'contacts.form.tab.notes': 'Amanqaku',
  'contacts.form.fullName': 'Igama elipheleleyo',
  'contacts.form.phone': 'Inombolo yomnxeba',
  'contacts.form.save': 'Gcina uqhagamshelwano',
  'contacts.bulk.count': '{{count}} ekhethiweyo',
  'contacts.bulk.add': 'Yongeza ithegi',
  'contacts.bulk.remove': 'Susa ithegi',
  'contacts.bulk.clear': 'Cima ukhetho',
  'contacts.tags.add': 'Yongeza ithegi',
  'contacts.showingOf': 'Kuboniswa {{count}} kwabangu-{{total}}',
  'contacts.filters.title': 'Izihluzi',
  'contacts.drawer.view': 'Uqhagamshelwano',
  'contacts.actions.message': 'Thumela umyalezo',
  'contacts.bulk.message': 'Thumela umyalezo',
  'contacts.fields.title': 'Iindawo zoqhagamshelwano',
  'contacts.fields.description': 'Iinkcukacha ezongezelelekileyo ozigcinayo ngomntu ngamnye ngaphandle kwegama, inombolo ne-imeyile. Indawo ivela kwifomu yoqhagamshelwano, kwisihluzi soluhlu nakumacandelo, kwaye ihamba nokungenisa nokukhupha.',
  'contacts.fields.new': 'Indawo entsha',
  'contacts.fields.edit': 'Hlela indawo',
  'contacts.fields.empty': 'Akukabikho ndawo zilungiselelweyo',
  'contacts.fields.emptyHint': 'Yongeza indawo yayo nantoni na efanele ukugcinwa ngomntu: inombolo ye-odolo, isicwangciso, umhla wokuhlaziya.',
  'contacts.fields.loadError': 'Ayikwazanga ukulayisha iindawo zoqhagamshelwano.',
  'contacts.fields.columns.label': 'Ileyibhile',
  'contacts.fields.columns.key': 'Isitshixo',
  'contacts.fields.columns.type': 'Uhlobo',
  'contacts.fields.columns.usage': 'Ukusetyenziswa',
  'contacts.fields.columns.active': 'Isimo',
  'contacts.fields.usage': '{{count}} abaqhagamshelwa',
  'contacts.fields.usage.one': '1 oqhagamshelwayo',
  'contacts.fields.active': 'Iyasebenza',
  'contacts.fields.inactive': 'Ifihliwe',
  'contacts.fields.type.text': 'Umbhalo',
  'contacts.fields.type.number': 'Inombolo',
  'contacts.fields.type.date': 'Umhla',
  'contacts.fields.type.boolean': 'Ewe / hayi',
  'contacts.fields.type.select': 'Ukhetho',
  'contacts.fields.type.url': 'Idilesi yewebhu',
  'contacts.fields.form.label': 'Ileyibhile',
  'contacts.fields.form.labelHint': 'Into indawo ebizwa ngayo kwifomu yoqhagamshelwano.',
  'contacts.fields.form.key': 'Isitshixo',
  'contacts.fields.form.keyHint': 'Silandela ileyibhile ude usitshintshe: oonobumba abancinci, amanani nemigca engaphantsi. Sibiza ikholamu ekungeniseni nasekukhupheni kunye nendawo yokubamba kwimiyalezo.',
  'contacts.fields.form.keyLocked': 'Isitshixo asinakutshintsha xa indawo sele ikho: lonke ixabiso eligcinwe kumntu oqhagamshelwayo lifakwe phantsi kwaso.',
  'contacts.fields.form.type': 'Uhlobo',
  'contacts.fields.form.typeHint': 'Indlela ixabiso eligcinwa ngayo, nendlela izihluzi namacandelo alithelekisa ngayo.',
  'contacts.fields.form.typeLocked': 'Uhlobo alunakutshintsha xa indawo sele ikho: amaxabiso asele egciniwe ebeya kuyeka ukuthetha into ebewayithetha.',
  'contacts.fields.form.options': 'Iinketho',
  'contacts.fields.form.optionsHint': 'Enye ngomgca. Umntu oqhagamshelwayo ubamba enye kuphela kwezi.',
  'contacts.fields.form.optionsRequired': 'Indawo yokhetho ifuna ubuncinane ukhetho olunye.',
  'contacts.fields.form.description': 'Inkcazelo',
  'contacts.fields.form.descriptionHint': 'Iboniswa phantsi kwendawo kwifomu yoqhagamshelwano.',
  'contacts.fields.form.active': 'Iyasebenza',
  'contacts.fields.form.activeHint': 'Ukucima indawo kuyifihla kwifomu yoqhagamshelwano nasekukhupheni. Akukho nto igcinwe kuye nawuphi na umntu oqhagamshelwayo echukunyiswayo.',
  'contacts.fields.form.keyInvalid': 'Sebenzisa oonobumba abancinci, amanani nemigca engaphantsi, uqale ngonobumba.',
  'contacts.fields.form.keyReserved': 'Wonke umntu oqhagamshelwayo sele enendawo enela gama.',
  'contacts.fields.form.keyTaken': 'Indawo enesi sitshixo sele ikho.',
  'contacts.fields.toast.created': 'Indawo “{{label}}” yongeziwe',
  'contacts.fields.toast.updated': 'Indawo “{{label}}” igciniwe',
  'contacts.fields.toast.deleted': 'Indawo “{{label}}” isusiwe',
  'contacts.fields.toast.saveFailed': 'Ayikwazanga ukugcina indawo',
  'contacts.fields.toast.deleteFailed': 'Ayikwazanga ukususa indawo',
  'contacts.fields.delete.title': 'Susa “{{label}}”?',
  'contacts.fields.delete.body': '{{count}} abaqhagamshelwa banexabiso lale ndawo. Ukuyisusa kucima loo maxabiso kubo bonke. Oku akunakubuyiselwa umva.',
  'contacts.fields.delete.bodyNone': 'Akukho mntu uqhagamshelwayo onexabiso lale ndawo okwangoku.',
  'contacts.fields.delete.hint': 'Ukufihla indawo ugcine amaxabiso, yicime endaweni yoko.',
  'contacts.fields.delete.confirm': 'Susa indawo',
  'contacts.form.tab.fields': 'Iindawo',
  'contacts.form.fields.empty': 'Akukabikho ndawo zilungiselelweyo',
  'contacts.form.fields.emptyHint': 'Chaza iinkcukacha ezongezelelekileyo ozigcinayo ngomntu ngamnye kwaye zivela apha.',
  'contacts.form.fields.manage': 'Seta iindawo zoqhagamshelwano',
  'contacts.form.fields.manageHint': 'Kulahleke indawo?',
  'contacts.form.fields.unset': 'Ayisetwanga',
  'contacts.form.fields.loadError': 'Ayikwazanga ukulayisha iindawo zoqhagamshelwano.',
  'contacts.form.fields.datePlaceholder': 'YYYY-MM-DD',
  'contacts.form.fields.urlPlaceholder': 'https://',
  'contacts.form.fields.invalid.number': 'Faka inombolo.',
  'contacts.form.fields.invalid.date': 'Faka umhla njenge-YYYY-MM-DD.',
  'contacts.form.fields.invalid.url': 'Faka idilesi yewebhu epheleleyo, eqala ngo-http:// okanye https://.',
  'contacts.form.fields.invalid.select': 'Khetha enye kwiinketho.',
  'contacts.form.fields.invalid.boolean': 'Khetha ewe okanye hayi.',
  'contacts.form.fields.invalid.unknown': 'Le ndawo ayisekho. Layisha kwakhona uzame kwakhona.',
  'contacts.filters.field': 'Indawo',
  'contacts.filters.field.any': 'Nayiphi na indawo',
  'contacts.filters.field.value': 'Ixabiso',
  'contacts.filters.field.valueAny': 'Naliphi na ixabiso',
  'contacts.filters.field.yes': 'Ewe',
  'contacts.filters.field.no': 'Hayi',
  'contacts.filters.field.placeholder': 'Chwetheza ixabiso',
  'contacts.import.preview.mapping': 'Iikholamu',
  'contacts.import.preview.column': 'Kwifayile',
  'contacts.import.preview.target': 'Iya ku',
  'contacts.import.preview.target.ignored': 'Ayihoywanga',
  'contacts.import.preview.target.unknown': 'Ayihambelani nanto',
  'contacts.import.preview.target.field': 'Indawo: {{label}}',
  'contacts.import.preview.createFields': 'Yenza iikholamu ezingu-{{count}} ezingahambelani nanto njengeendawo zombhalo',
  'contacts.import.preview.createFieldsHint': 'Nganye iba yindawo elungiselelweyo ebizwa ngesihloko sayo, kwaye amaxabiso ayo angeniswa nezinye. Yiyeke icimile ukuze utsibe ezo kholamu.',
  'contacts.import.preview.fieldProblems': '{{count}} amaxabiso akahambelani nohlobo lwendawo yawo. Aza kugcinwa njengombhalo.',
  'contacts.import.preview.fieldProblem': 'Umgca {{row}}, {{column}}: “{{value}}”',
  'contacts.import.doneFieldProblems': '{{count}} amaxabiso akahambelananga nohlobo lwendawo yawo kwaye agcinwe njengombhalo.',
  'contacts.import.format.customFields': 'Nayiphi na enye ikholamu idityaniswa nendawo elungiselelweyo ngesitshixo sayo okanye ileyibhile yayo. Iikholamu ezingahambelani nanto zingenziwa njengeendawo zombhalo ukusuka kumboniso wangaphambili.',
  'contacts.import.csv.col.notes': 'Amanqaku ombhalo okhululekileyo agcinwe ngomntu oqhagamshelwayo.',
  'contacts.import.format.intro': 'Umgca wokuqala kufuneka ube sisihloko esibiza ikholamu nganye. Ezi zezona kholamu umngenisi azaziyo; nasiphi na esinye isihloko sidityaniswa nendawo elungiselelweyo.',
  'contacts.importCard.columns': 'Iikholamu: msisdn, name, email, locale, tags, notes, kunye nenye ngendawo nganye elungiselelweyo. Yahlula iithegi ngesemikholoni.'
};

export default contacts;
